import { useState, useEffect, useCallback, useMemo, useRef } from 'react';

const cache = new Map();

function trimTrailingSlash(value = '') {
  if (!value) return '';
  return value.endsWith('/') ? value.slice(0, -1) : value;
}

function resolveApiBase() {
  const envBase = trimTrailingSlash(process.env.REACT_APP_API_URL || '');
  if (envBase) {
    return envBase;
  }

  if (typeof window !== 'undefined') {
    const { protocol, hostname, port } = window.location;
    let effectivePort = '';
    if (port && port !== '80' && port !== '443') {
      effectivePort = port === '3000' ? '5000' : port;
    }
    const hostWithPort = effectivePort ? `${hostname}:${effectivePort}` : hostname;
    return `${protocol}//${hostWithPort}`;
  }

  return '';
}

function parseSnapshotDate(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return date.toISOString();
}

export function useLegalDocument(docType) {
  const apiBase = useMemo(resolveApiBase, []);
  const [state, setState] = useState({
    loading: true,
    content: '',
    error: null,
    updatedAt: null,
    contentHash: null,
    docType
  });
  const [requestKey, setRequestKey] = useState(0);
  const cacheKeyRef = useRef(null);

  const refetch = useCallback(() => {
    setRequestKey((key) => key + 1);
  }, []);

  useEffect(() => {
    if (!docType) return;
    if (!apiBase) {
      setState((prev) => ({
        ...prev,
        loading: false,
        error: 'Legal server is not configured.'
      }));
      return;
    }

    const cacheKey = `${apiBase}::${docType}`;
    cacheKeyRef.current = cacheKey;
    const cached = cache.get(cacheKey);
    if (cached && requestKey === 0) {
      setState({
        loading: false,
        content: cached.content,
        error: null,
        updatedAt: cached.updatedAt,
        contentHash: cached.contentHash,
        docType: cached.docType
      });
      return;
    }

    let active = true;

    async function loadDocument() {
      setState((prev) => ({
        ...prev,
        loading: true,
        error: null,
        docType
      }));

      try {
        const response = await fetch(
          `${apiBase}/api/legal?doc=${encodeURIComponent(docType)}`
        );
        const payload = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error(payload?.error || `Failed to load ${docType}.`);
        }

        if (!active) return;

        const nextState = {
          loading: false,
          content: payload?.content || '',
          error: null,
          updatedAt: parseSnapshotDate(payload?.createdAt || payload?.created_at),
          contentHash: payload?.contentHash || payload?.content_hash || null,
          docType: payload?.docType || payload?.doc_type || docType
        };

        setState(nextState);

        cache.set(cacheKeyRef.current, nextState);
      } catch (error) {
        if (!active) return;
        setState((prev) => ({
          ...prev,
          loading: false,
          error: error.message || `Failed to load ${docType}.`
        }));
      }
    }

    loadDocument();

    return () => {
      active = false;
    };
  }, [apiBase, docType, requestKey]);

  return {
    ...state,
    refetch
  };
}
